import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-light-svg-icons/faCircleExclamation';
import { resolveSmartLinkPlatformDomain } from 'fym-shared/src/smartlinks/domains';
import type { OnboardingIntent } from 'fym-shared/src/music24/onboardingIntent';
import type { OnboardingStateAnswer } from '../../../../../music24-api/src/trpc/onboarding.router';
import { TypedMemo } from '../../common/types';
import { AddArtistModal } from '../../accountSelector/AddArtistModal';
import { FullScreenUpgradeHost } from '../../music24/components/SMLUpgradeModal/FullScreenUpgradeHost';
import { useWelcomeForm } from './useWelcomeForm';
import { useFinishWelcome } from './useFinishWelcome';
import { WelcomeHeadline } from './WelcomeHeadline';
import { ArtistNameField } from './ArtistNameField';
import { ArtistAddressPreview } from './ArtistAddressPreview';
import { WelcomeActions } from './WelcomeActions';
import { WelcomeLoop } from './WelcomeLoop';
import { WelcomeTrialNote } from './WelcomeTrialNote';
import { SongsBrewTickBox } from './songsBrew/SongsBrewTickBox';
import { useSongsBrewTickBox } from './songsBrew/useSongsBrewTickBox';
import { welcomeMessages as messages } from './welcomeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole welcome page; each part uses its own slice of it */
import styles from './WelcomeView.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface OwnerWelcomeProps {
  state: OnboardingStateAnswer;
  intent: OnboardingIntent | null;
  onExample: () => void;
  onAddSong: () => void;
  /** Loads `onboarding.state` again; used after the Add artist dialog. */
  refetch: () => Promise<{ data?: OnboardingStateAnswer }>;
}

/**
 * The owner variant (design: onboarding a1-a6, a8): the artist name, the address and the two
 * ways in. Both buttons save the name and the address first, then mark the welcome as seen.
 * The SongsBrew tick box sits between the address and the buttons (design: songsbrew-promotion b1,
 * b2); its value goes with `finishWelcome`.
 */
const OwnerWelcomeComponent: React.FC<OwnerWelcomeProps> = ({ state, intent, onExample, onAddSong, refetch }) => {
  const intl = useIntl();
  const form = useWelcomeForm(state);
  const finishWelcome = useFinishWelcome();
  const [isBusy, setIsBusy] = useState(false);
  const [isAddArtistOpen, setAddArtistOpen] = useState(false);
  const songsBrew = useSongsBrewTickBox();
  const { subscribeSongsBrew } = songsBrew;
  const { artist } = state;
  // An API from before the field sends nothing: no note then.
  const trialSkipped = state.trialSkipped ?? null;

  const saveAndFinish = useCallback(
    async (next: () => void): Promise<void> => {
      setIsBusy(true);
      const saved = await form.save();
      if (!saved) {
        setIsBusy(false);
        return;
      }
      await finishWelcome({ subscribeSongsBrew });
      setIsBusy(false);
      next();
    },
    [finishWelcome, form, subscribeSongsBrew]
  );

  const handleExample = useCallback(() => {
    saveAndFinish(onExample).catch(console.error);
  }, [onExample, saveAndFinish]);

  const handleAddSong = useCallback(() => {
    // design: onboarding a1 note. With no artist, "Add my song" first opens the Add artist dialog.
    if (artist === null) {
      setAddArtistOpen(true);
      return;
    }
    saveAndFinish(onAddSong).catch(console.error);
  }, [artist, onAddSong, saveAndFinish]);

  const closeAddArtist = useCallback(() => {
    setAddArtistOpen(false);
    refetch()
      .then(async (result) => {
        if (result.data?.artist === null || result.data?.artist === undefined) {
          return;
        }
        await finishWelcome({ subscribeSongsBrew });
        onAddSong();
      })
      .catch(console.error);
  }, [finishWelcome, onAddSong, refetch, subscribeSongsBrew]);

  return (
    <main className={styles.grid}>
      <WelcomeHeadline intent={intent} />
      <div className={styles.body}>
        {trialSkipped === 'alreadyUsed' ? <WelcomeTrialNote /> : null}
        {artist !== null ? (
          <>
            <ArtistNameField name={form.name} setName={form.setName} disabled={isBusy} needsName={form.needsName} />
            <ArtistAddressPreview
              slug={form.slug}
              setSlug={form.setSlug}
              domain={resolveSmartLinkPlatformDomain(null, artist.platformDomain)}
              slugState={form.slugState}
              slugProblem={form.slugProblem}
              slugRefusal={form.slugRefusal}
              suggestion={form.suggestion}
              error={form.error}
              disabled={isBusy}
            />
          </>
        ) : null}
        {form.error === 'network' ? (
          <div className={styles.alertError} role="alert">
            <FontAwesomeIcon icon={faCircleExclamation} />
            <span>{intl.formatMessage(messages.errorNetwork)}</span>
          </div>
        ) : null}
        {songsBrew.isShown && (
          <SongsBrewTickBox checked={songsBrew.isTicked} onChange={songsBrew.setTicked} disabled={isBusy} />
        )}
        <WelcomeActions onExample={handleExample} onAddSong={handleAddSong} disabled={!form.canSave} isBusy={isBusy} />
      </div>
      <WelcomeLoop />
      <AddArtistModal isOpen={isAddArtistOpen} onClose={closeAddArtist} />
      {/* The welcome page has no SMLContainer: "See the plans" needs its own plan picker. */}
      {trialSkipped === 'alreadyUsed' ? <FullScreenUpgradeHost /> : null}
    </main>
  );
};

export const OwnerWelcome = TypedMemo(OwnerWelcomeComponent);
